import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Modal from '../common/Modal';
import Button from '../common/Button';
import { LoadingSpinner } from '../common/LoadingSpinner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FollowUpTasksModal = ({ isOpen, onClose, parentTask }) => {
  const [followUpTasks, setFollowUpTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen && parentTask?.id) {
      fetchFollowUpTasks();
    }
  }, [isOpen, parentTask]);

  const fetchFollowUpTasks = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await axios.get(`${API}/tasks`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        params: {
          parent_task_id: parentTask.id
        }
      });
      
      setFollowUpTasks(response.data.filter(task => task.parent_task_id === parentTask.id));
    } catch (error) {
      console.error('Error fetching follow-up tasks:', error);
      setError('Failed to load follow-up tasks');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (task) => {
    if (task.completed) return 'text-green-600 bg-green-100';
    if (task.priority === 'High') return 'text-red-600 bg-red-100';
    if (task.priority === 'Medium') return 'text-yellow-600 bg-yellow-100';
    return 'text-gray-600 bg-gray-100';
  };

  const handleTaskClick = (task) => {
    // Close this modal and navigate to the task
    onClose();
    window.location.href = `/tasks/${task.id}`;
  };

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Follow-up Tasks`}
      size="lg"
    >
      <div className="p-6">
        {/* Parent Task Info */}
        <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <h3 className="font-semibold text-blue-900 mb-2">📋 Parent Task</h3>
          <p className="text-blue-800">{parentTask?.title}</p>
          {parentTask?.completed && (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 mt-2">
              ✅ Completed
            </span>
          )}
        </div>

        {/* Follow-up Tasks List */}
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <LoadingSpinner />
            <span className="ml-3 text-gray-600">Loading follow-up tasks...</span>
          </div>
        ) : error ? (
          <div className="text-center h-32 flex items-center justify-center">
            <div className="text-red-600">
              <div className="text-2xl mb-2">⚠️</div>
              <p>{error}</p>
            </div>
          </div>
        ) : followUpTasks.length === 0 ? (
          <div className="text-center h-32 flex items-center justify-center">
            <div className="text-gray-500">
              <div className="text-2xl mb-2">📝</div>
              <p>No follow-up tasks found</p>
            </div>
          </div>
        ) : (
          <div>
            <h3 className="font-semibold text-gray-900 mb-4">
              📋 Follow-up Tasks ({followUpTasks.length})
            </h3>
            
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {followUpTasks.map((task) => (
                <div
                  key={task.id}
                  onClick={() => handleTaskClick(task)}
                  className="p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 cursor-pointer transition-all"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900 mb-1">{task.title}</h4>
                      
                      <div className="flex items-center space-x-4 text-sm text-gray-600 mb-2">
                        {task.due_date && (
                          <span className="flex items-center">
                            📅 {new Date(task.due_date).toLocaleDateString()}
                          </span>
                        )}
                        
                        {task.category && (
                          <span className="flex items-center">
                            🏷️ {task.category}
                          </span>
                        )}
                      </div>
                      
                      {task.notes && (
                        <p className="text-sm text-gray-600 truncate">
                          {task.notes.length > 100 ? `${task.notes.substring(0, 100)}...` : task.notes}
                        </p>
                      )}
                    </div>
                    
                    <div className="flex flex-col items-end space-y-2 ml-4">
                      {/* Status Badge */}
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(task)}`}>
                        {task.completed ? '✅ Done' : `${task.priority} Priority`}
                      </span>
                      
                      {/* Navigation Arrow */}
                      <div className="text-blue-500">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="mt-6 pt-4 border-t">
          <Button
            type="button"
            fullWidth
            onClick={onClose}
            variant="secondary"
          >
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default FollowUpTasksModal;